import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";
import { get, size, cloneDeep, forEach, filter, isEmpty } from "lodash";

import Box from "@material-ui/core/Box";

import SiteLogTable from "./SiteLogTable";
import Error from "../Common/Error";

import { fetchLogs } from "../../utils/eventGroups";
import {
  resetTabLogs,
  fetchLogsTabNew,
} from "../../actions/siteEntranceExit/tabs";
import { driveInOut, walkInOut, trainInOut } from "../../constants/reducers";
import { LOG_EVENT_GROUP_TYPES } from "../../constants/logEventGroupTypes";
import colors from "../../assets/style/colors";
import {
  initialFiltersDriveInOut,
  initialFiltersWalkInOut,
  initialFiltersTrainInOut,
} from "../../constants/filters";
import {
  driveInOutFields,
  walkInOutFields,
  trainInOutFields,
} from "../../resources/SiteLogFields";

const styles = (theme) => ({
  mainContent: {
    boxShadow: "0px 0px 4px 0px " + colors.black23,
    borderRadius: "4px",
    backgroundColor: colors.white,
    [theme.breakpoints.down("lg")]: {
      minWidth: "calc(100vw - 200px)",
    },
    [theme.breakpoints.up("lg")]: {
      minWidth: "1100px",
    },
  },
});

const INITIAL_FILTERS = {
  walkInOut: initialFiltersWalkInOut,
  driveInOut: initialFiltersDriveInOut,
  trainInOut: initialFiltersTrainInOut,
}

const displayColumns = {
  trainInOut: {
    0: Array.from(Array(size(get(trainInOutFields, ["subFields", 0]))).keys()),
    1: Array.from(Array(size(get(trainInOutFields, ["subFields", 1]))).keys()),
  },
  driveInOut: {
    0: Array.from(Array(size(get(driveInOutFields, ["subFields", 0]))).keys()),
    1: Array.from(Array(size(get(driveInOutFields, ["subFields", 1]))).keys()),
    2: Array.from(Array(size(get(driveInOutFields, ["subFields", 2]))).keys()),
    3: Array.from(Array(size(get(driveInOutFields, ["subFields", 3]))).keys()),
    4: Array.from(Array(size(get(driveInOutFields, ["subFields", 3]))).keys()),
    5: Array.from(Array(size(get(driveInOutFields, ["subFields", 3]))).keys()),
    6: Array.from(Array(size(get(driveInOutFields, ["subFields", 3]))).keys()),
    7: Array.from(Array(size(get(driveInOutFields, ["subFields", 4]))).keys()),
  },
  walkInOut: {
    0: Array.from(Array(size(get(walkInOutFields, ["subFields", 0]))).keys()),
    1: Array.from(Array(size(get(walkInOutFields, ["subFields", 1]))).keys()),
    2: Array.from(Array(size(get(walkInOutFields, ["subFields", 2]))).keys()),
  },
};

const SiteLog = (props) => {
  const { classes, logs, site, page, section } = props;
  const logTypes = get(LOG_EVENT_GROUP_TYPES, section);

  const rowsPerPage = get(props, [
    "siteEntranceExit",
    section,
    "tabs",
    0,
    "logs",
    "rowsPerPage",
  ]);

  React.useEffect(() => {
    if (rowsPerPage) {
      // If there are no filters in the store we initiate the filters, else they'll be used
      const initialFilters = get(INITIAL_FILTERS, section);
      props.dispatch(
        fetchLogsTabNew(
          section,
          0,
          get(props, [
            "siteEntranceExit",
            section,
            "tabs",
            0,
            "logs",
            "filters",
          ])
            ? null
            : initialFilters,
          page
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const updateData = (
    offset = 0,
    limit = 5,
    force = false,
    cleanLogs = false
  ) => {
    if (cleanLogs) {
      props.dispatch(resetTabLogs(section, 0));
    }
    fetchLogs(
      props.dispatch,
      section,
      0,
      site.zones.map((z) => z.id),
      logTypes,
      limit + 5,
      offset,
      force
    );
  };

  const getDisplayColumns = () => {
    let columns = cloneDeep(displayColumns[section]);
    if (section === driveInOut) {
      const colIdMap = {
        trailer1: 3,
        trailer2: 4,
        container1: 5,
        container2: 6,
      };
      forEach(colIdMap, (v, k) => {
        if (
          size(
            filter(get(logs, ["logs"]), (item) => !isEmpty(get(item, [k])))
          ) === 0
        ) {
          delete columns[v];
        }
      });
    }
    return columns;
  };

  return (
    <Box className={classes.mainContent}>
      {!site ? (
        <Error
          noDecoration={true}
          height="calc(100vh - 232px)"
          error={I18n.get("site_pleaseSelectSite")}
        />
      ) : logs.error ? (
        <Error
          noDecoration={true}
          height="calc(100vh - 232px)"
          error={logs.error}
        />
      ) : !get(props, [
          "siteEntranceExit",
          section,
          "tabs",
          0,
          "logs",
          "rowsPerPage",
        ]) ? (
        <></>
      ) : (
        <SiteLogTable
          section={section}
          tabIndex={0}
          openedTabsNb={props.openedTabsNb}
          updateHistory={updateData}
          logs={logs.filteredLogs || []}
          sort={logs.sort || {}}
          short={false}
          totalRows={logs.total || 0}
          fetchMoreData={() =>
            props.dispatch(fetchLogsTabNew(section, 0, null))
          }
          filtered={
            logs.filteredLogs && logs.logs
              ? logs.filteredLogs.length !== logs.logs.length
              : false
          }
          filters={logs.filters || {}}
          isLoading={logs.isFetching}
          pageNb={page}
          rowsPerPage={logs.rowsPerPage || 10}
          open={logs.collapsed}
          displayColumns={getDisplayColumns()}
        />
      )}
    </Box>
  );
};

SiteLog.propTypes = {
  logs: PropTypes.object,
  site: PropTypes.object,
  dynamicTabsNb: PropTypes.number,
  page: PropTypes.number.isRequired,
};

const mapStateToProps = (state) => {
  return {
    userDataStore: state.userDataStore,
    siteEntranceExit: state.siteEntranceExit,
  };
};

export default connect(mapStateToProps)(withStyles(styles)(SiteLog));
